/**
 * Hook: Initial Viewport Positioning
 *
 * This hook positions the canvas viewport when the flow is first opened.
 * It ensures the root node (main data source) is visible and centered-left
 * in the viewport with appropriate padding.
 *
 * Behavior:
 * - Runs once when flow opens (tracked by `fitted` state)
 * - Finds the root node (main source, not a flow, not secondary)
 * - Waits for node measurement (width/height available)
 * - Calculates viewport position to center root node vertically, left-aligned with padding
 * - Adjusts padding if secondary flows exist (to show connecting edges)
 *
 * Positioning Formula:
 * - viewportX = paddingLeft - rootNode.position.x
 *   (Moves viewport left by padding amount, accounting for node's x position)
 * - viewportY = (viewportHeight / 2) - rootNode.measured.height - rootNode.position.y
 *   (Centers node vertically in viewport)
 *
 * Padding:
 * - Default: 40px (space for UI elements on left)
 * - With secondary flows: 160px (additional space to show edges connecting to secondary flows)
 *
 * Special Cases:
 * - No root node: Hook doesn't run (waits for node to appear)
 * - No measurement: Hook doesn't run (waits for React Flow to measure node)
 *
 * @param flowElement - The container element (for viewport height calculation)
 * @returns Whether the viewport has been fitted (true after first fit)
 *
 * @see docs/FlowNodesArchitecture.md - Initial viewport positioning section
 * @see docs/FlowNodesModificationGuide.md - How to modify initial positioning
 */
import { useNodes, useReactFlow } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { placeFlowInViewport } from '../placeFlowInViewport';

export default function useInitialFitView(
  flowElement: HTMLDivElement | null,
  layoutDirection: 'LR' | 'TB' = 'LR',
) {
  const flowInstance = useReactFlow();
  /**
   * Track whether we've already fitted the viewport.
   *
   * This prevents the hook from running multiple times and causing
   * viewport jumps. Once fitted, we don't run again.
   */
  const [fitted, setFitted] = useState(false);
  const internalNodes = useNodes();

  useEffect(() => {
    // We only want to fit the nodes once
    if (fitted) return;

    /**
     * Skip fitView for multi-flow expanded views.
     *
     * When there are multiple independent flows (like in project expanded view),
     * we don't want to auto-center/zoom because:
     * 1. It causes a jarring "jump" animation
     * 2. Flows are already properly positioned vertically
     * 3. The user needs to see all flows at once, not centered on one
     */
    const independentFlowNodes = internalNodes.filter(
      (node) => node.type === 'flow' && !node.parentId,
    );

    if (independentFlowNodes.length > 1) {
      // Mark as fitted to prevent this hook from running again
      setFitted(true);
      return;
    }

    /**
     * Find the root node (main data source).
     *
     * Root node criteria:
     * - data.isRoot === true (marked as root in mappers)
     * - !data.isSecondaryFlow (not a secondary flow node)
     * - type !== 'flow' (not a flow container, but the actual resource)
     *
     * This is typically the main data source that starts the flow.
     */
    let rootNode = internalNodes.find(
      ({ data, type }) => data.isRoot && !data.isSecondaryFlow && type !== 'flow',
    );

    /**
     * If no root node found, try finding a shared dataset node
     */
    if (!rootNode) {
      rootNode = internalNodes.find(
        ({ data, type }) =>
          (data?.item as any)?.dataSetType === 'shared' && !data.isSecondaryFlow && type !== 'flow',
      );
    }

    /**
     * Check if there are secondary flows (linked/triggered flows).
     *
     * If secondary flows exist, we need more left padding to show the
     * edges that connect the main flow to secondary flows.
     */
    const hasFlowNodes = internalNodes.some(({ type }) => type === 'flow');
    const hasScheduleNodes = internalNodes.some(
      ({ type }) => type === 'cron_job' || type === 'last_run',
    );

    /**
     * Wait for node to be measured before fitting.
     *
     * Conditions:
     * 1. Root node exists
     * 2. Root node has been measured (width/height available)
     */
    if (rootNode?.measured?.width && rootNode.measured.height) {
      /**
       * Calculate left padding.
       *
       * Default: 40px for UI elements
       * With secondary flows: 160px to show connecting edges
       * With schedule nodes (LR): 200px to show cron/run-now to the left of source
       *
       * To modify padding, change these values.
       */
      let paddingLeft = 40;

      // if there are secondary flows, increase the padding to show the edges
      if (hasFlowNodes) {
        paddingLeft = 160;
      }

      // In LR mode, schedule nodes are positioned to the left of the source.
      // Increase padding so they are visible on initial render.
      if (hasScheduleNodes && layoutDirection !== 'TB') {
        paddingLeft = Math.max(paddingLeft, 200);
      }

      /**
       * Calculate viewport position.
       *
       * The viewport position is calculated to:
       * - Show root node centered vertically
       * - Show root node with left padding
       * - Account for node's actual position and size
       *
       * Formula explanation:
       * - viewportX: Move viewport left by padding amount, accounting for node's x position
       * - viewportY: Center node vertically (half viewport height, minus node height, minus node y)
       */
      const viewportHeight = flowElement?.clientHeight;
      const viewportWidth = flowElement?.clientWidth;
      const isTB = layoutDirection === 'TB';

      let useViewportX: number;
      let viewportY: number;

      if (rootNode.type === 'byoSourceShell') {
        // BYO create has only 3 small nodes — fitView centers them rather than
        // left-aligning (which is the design for large multi-node flows).
        flowInstance.fitView({ padding: 0.2, maxZoom: 0.9, minZoom: 0.9 });
        setFitted(true);
        return;
      }

      if (isTB) {
        placeFlowInViewport(flowInstance, internalNodes, true, {
          viewportWidth,
          viewportHeight,
          fallback: () => flowInstance.fitView({ padding: 0.2, maxZoom: 0.9, minZoom: 0.9 }),
        });
        setFitted(true);
        return;
      } else {
        // LR: left-aligned with vertical centering
        const viewportX = paddingLeft - rootNode.position.x;
        viewportY = viewportHeight / 2 - rootNode.measured.height - rootNode.position.y;

        const hasExtremeOrNegativePosition = viewportX < 0 || viewportX > 500;
        useViewportX = hasExtremeOrNegativePosition ? paddingLeft : viewportX;
      }

      flowInstance.setViewport({ x: useViewportX, y: viewportY, zoom: 0.9 });

      setFitted(true); // Mark as fitted to prevent re-running, it will prevent the blur effect
    }
  }, [internalNodes]);

  return fitted;
}
